import {useAuthInfo} from "@propelauth/react";
import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";

import {API_URL, fetchWithToken, putWithToken} from "../../api/apiBase";
import {Card, CardContent, CardHeader, CardTitle} from "../ui/card";
import {Form, FormControl, FormField, FormItem, FormLabel} from "../ui/form";
import {Input} from "../ui/input";
import {Button} from "../ui/button";
import {useToast} from "../../hooks/use-toast";

interface SharePointIntegrationView {
    tenant_id: string;
    client_id: string;
    has_secret: boolean;
}

interface SharePointIntegrationWrite {
    tenant_id: string;
    client_id: string;
    client_secret: string;
}

/**
 * Entra (azure) app registration used to push bid files to sharepoint.
 * the secret is write only - server never sends it back.
 * */
export function SharePointIntegrationCard({company_id}: { company_id: string }) {
    const {accessToken} = useAuthInfo();
    const {toast} = useToast();
    const [hasSecret, setHasSecret] = useState(false);

    const form = useForm<SharePointIntegrationWrite>({
        defaultValues: {tenant_id: '', client_id: '', client_secret: ''}
    });

    useEffect(() => {
        fetchWithToken(`/integration/sharepoint/company/${company_id}`, accessToken as string)
            .then((data: SharePointIntegrationView | null) => {
                if (data) {
                    form.reset({tenant_id: data.tenant_id, client_id: data.client_id, client_secret: ''});
                    setHasSecret(data.has_secret);
                }
            }).catch((err) => console.log('sharepoint integration', err))
    }, [company_id])

    const onSave = (data: SharePointIntegrationWrite) => {
        putWithToken(`/integration/sharepoint/company/${company_id}`, JSON.stringify(data), accessToken as string)
            .then((res: SharePointIntegrationView) => {
                setHasSecret(res.has_secret);
                form.setValue('client_secret', '');
                toast({title: "Saved", description: "SharePoint integration saved"});
            }).catch((err) => {
            toast({title: "Error", description: "Failed to save SharePoint integration", variant: "destructive"});
        })
    }

    const onTest = async () => {
        // plain fetch so we can show the server's error message
        const res = await fetch(`${API_URL}/integration/sharepoint/company/${company_id}/test`, {
            method: "POST",
            headers: {Authorization: `Bearer ${accessToken}`},
        });
        const body = await res.json().catch(() => ({}));
        if (res.ok) {
            toast({title: "Connected", description: "Credentials are valid"});
        } else {
            toast({title: "Connection failed", description: body?.message || res.statusText, variant: "destructive"});
        }
    }

    const textField = (name: keyof SharePointIntegrationWrite, label: string, type = "text", placeholder = "") =>
        <FormField
            control={form.control}
            name={name}
            render={({field}) => (
                <FormItem>
                    <FormLabel>{label}</FormLabel>
                    <FormControl>
                        <Input type={type} placeholder={placeholder} autoComplete="off" {...field}/>
                    </FormControl>
                </FormItem>
            )}
        />

    return <Card className="my-4">
        <Form {...form}>
            <form onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit(onSave)();
            }}>
                <CardHeader>
                    <div className="flex columns-2 justify-between">
                        <CardTitle>SharePoint Integration</CardTitle>
                        <div className="flex gap-2">
                            <Button variant="outline" type="button" onClick={onTest}>Test connection</Button>
                            <Button variant="outline" type="submit">Save</Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                        Entra app registration used to push bid files to SharePoint. The app needs the
                        Microsoft Graph <strong>application</strong> permission Sites.ReadWrite.All
                        (or Sites.Selected) with admin consent.
                    </p>
                    {textField("tenant_id", "Tenant ID")}
                    {textField("client_id", "Client (Application) ID")}
                    {textField("client_secret", "Client Secret", "password",
                        hasSecret ? "saved - leave blank to keep" : "")}
                </CardContent>
            </form>
        </Form>
    </Card>
}
